import React, { useCallback, useState, useEffect } from 'react'
import {
  hooks,
  PhoneVerification,
  useApplicationContext,
  useProgressiveCapture,
  useBuyingCriteriaModal,
} from '@adc/ui-adc'
import * as blueprint from '@adc/blueprint'
import { useListingContext } from '../context/listing'

const hasUserSelectedOptin = userPhones => {
  if (!userPhones) return true
  if (!userPhones.length) return false
  const latestPhone = PhoneVerification.utils.getLatestPhone(userPhones)
  return typeof latestPhone.opt_in === 'boolean'
}

export const withNudgeHook = WrappedComponent => originalProps => {
  const { user, openOptInModal, featureFlags } = originalProps
  const [shouldDisplayModal, setShouldDisplayModal] = useState(false)
  const { data, actions } = hooks.useWebApplicationNudges()
  const { listing } = useListingContext()
  const { nudge } = actions

  const isPropertySaved = listing?.primary_property?.is_currently_saved

  const isModalEnabledToDisplay = useCallback(() => {
    if (!isPropertySaved || hasUserSelectedOptin(user.party?.phones)) {
      return false
    }
    return true
  }, [isPropertySaved, user.party])

  useEffect(() => {
    if (shouldDisplayModal) {
      if (data.active.has('sms_opt_in') && isModalEnabledToDisplay()) {
        openOptInModal()
        setShouldDisplayModal(false)
      }
    }
  }, [
    shouldDisplayModal,
    data.active,
    openOptInModal,
    setShouldDisplayModal,
    isModalEnabledToDisplay,
  ])

  useEffect(() => {
    if (shouldDisplayModal && isModalEnabledToDisplay()) {
      nudge('sms_opt_in')
    }
  }, [shouldDisplayModal, isModalEnabledToDisplay, nudge])

  // Progressive capture for buying preferences
  const ctx = useApplicationContext()
  const criteriaOptions =
    blueprint.user.hooks.useBuyingCriteriaOptionsFromContext({
      user: ctx?.user,
      updateUser: fields => ctx?.actions?.updateUser(fields),
    })
  const { data: criteriaData, actions: criteriaActions } =
    blueprint.user.hooks.useBuyingCriteria({
      ...criteriaOptions,
      autoSave: true,
    })

  const { openBuyingCriteriaModal } = useBuyingCriteriaModal({
    data: criteriaData,
    actions: criteriaActions,
  })

  const { nudgeAfterSave } = useProgressiveCapture({
    preferences: criteriaData?.criteria ?? null,
    preferencesOptions: criteriaOptions,
    onAnswerMore: openBuyingCriteriaModal,
    featureFlags,
  })

  const nudgeOptInModal = () => {
    setShouldDisplayModal(true)
    nudgeAfterSave()
  }

  return (
    <WrappedComponent {...originalProps} nudgeOptInModal={nudgeOptInModal} />
  )
}
